import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  Save, Pin, Plus, Loader2
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NavigationBar } from '../components/ui/NavigationBar';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { AlertModal } from '../components/ui/AlertModal';
import { RichTextEditor } from '../components/ui/RichTextEditor';
import { ImageUpload, ImageUploadHandle } from '../components/ui/ImageUpload';
import { SparePartFormV2, SparePartFormHandle } from './SparePartFormV2';
import type { SparePartFormData } from '../types/spareParts';
import { theme } from '../lib/theme';
import planLimitsData from '../data/planLimits.json';
import { useTranslations } from '../i18n';
import { deleteTempFiles } from '../lib/r2';
import { useQueryClient } from '@tanstack/react-query';
import { NOTE_KEYS, PART_KEYS, GALLERY_KEYS, REMINDER_KEYS } from '../hooks/useCarData';
import { ReminderFormSection } from '../components/cars/ReminderFormSection';
import type { ReminderFormData } from '../types/reminders';

interface NoteFormProps {
  carId: string;
  noteId?: string;
  onClose: () => void;
  isReadOnly?: boolean;
}

export function NoteForm({ carId, noteId, onClose, isReadOnly = false }: NoteFormProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const outletContext = useOutletContext<any>() || {};
  const { t } = useTranslations();
  const { slug } = useParams();
  const effectiveUserId = outletContext.effectiveUserId || user?.id;
  const isAdminView = outletContext.isAdminView || false;

  const effectivePreferences = outletContext.ownerPreferences || outletContext.preferences;
  const planType = effectivePreferences?.plan_type || 'free';
  const planConfig = (planLimitsData as any)[planType] || (planLimitsData as any)['free'];
  const countLimit = (effectivePreferences?.file_count_limit ?? 0) > 0 ? effectivePreferences!.file_count_limit : planConfig.file_count_limit;

  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isPinned, setIsPinned] = useState(false); // Added for pinned notes
  const [images, setImages] = useState<Array<{ url: string; thumbnail?: string; comment?: string; sizeBytes?: number; is_receipt?: boolean }>>([]);
  const [spareParts, setSpareParts] = useState<SparePartFormData[]>([]);
  const [initialImages, setInitialImages] = useState<string>('[]');
  const [initialSpareParts, setInitialSpareParts] = useState<string>('[]');
  const [saving, setSaving] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [car, setCar] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [reminderData, setReminderData] = useState<ReminderFormData | null>(null);
  const [initialReminderData, setInitialReminderData] = useState<string>('null');
  const [actualReminderId, setActualReminderId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const imageUploadRef = useRef<ImageUploadHandle>(null);
  const sparePartFormRef = useRef<SparePartFormHandle>(null);
  const savedImageUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [noteId]);


  const loadData = async () => {
    setLoading(true);
    try {
      // Load Car
      const { data: carData } = await supabase
        .from('cars')
        .select('*')
        .eq('id', carId)
        .single();

      setCar(carData);

      // Load Note and related data if it exists
      if (noteId) {
        // We can load everything in parallel for better speed
        const [noteRes, imagesRes, partsRes] = await Promise.all([
          supabase.from('notes').select('*').eq('id', noteId).single(),
          supabase.from('note_images').select('image_url, image_url_thumb, comment, file_size_bytes, is_receipt').eq('note_id', noteId).order('display_order'),
          supabase.from('note_spare_parts').select(`
            quantity, price_per_unit, comment, added_at,
            spare_parts (
              id, name, part_code, link, comment, created_at,
              total_uses, avg_price, last_used_at,
              spare_part_images ( * )
            )
          `).eq('note_id', noteId)
        ]);

        if (noteRes.error) throw noteRes.error;

        const noteData = noteRes.data;
        if (noteData) {
          setContent(noteData.content || '');
          setIsPublic(noteData.is_public);
          setIsPinned(noteData.is_pinned || false);
          setInitialValues({
            title: noteData.title,
            content: noteData.content || '',
            isPublic: noteData.is_public,
            isPinned: noteData.is_pinned || false,
            isTemporary: noteData.is_temporary || false
          });
        }

        // Process images
        if (imagesRes.data) {
          const imgData = imagesRes.data.map(img => ({
            url: img.image_url,
            thumbnail: img.image_url_thumb,
            comment: img.comment,
            sizeBytes: img.file_size_bytes,
            is_receipt: !!img.is_receipt
          }));
          setImages(imgData);
          setInitialImages(JSON.stringify(imgData));
          savedImageUrlsRef.current = new Set(imgData.map(img => img.url));
        }

        // Process spare parts
        if (partsRes.data) {
          const parts: SparePartFormData[] = partsRes.data.map((link: any) => {
            const part = link.spare_parts;
            const images = part.spare_part_images?.sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0)).map((img: any) => ({
              url: img.image_url,
              thumbnail: img.image_url_thumb,
              comment: img.comment || '',
              sizeBytes: img.file_size_bytes,
              is_receipt: !!img.is_receipt,
              log_entry_id: img.log_entry_id,
              note_id: img.note_id
            })) || [];

            return {
              id: part.id,
              name: part.name,
              part_code: part.part_code || undefined,
              link: part.link || undefined,
              master_comment: part.comment || undefined,
              images: images,
              quantity: link.quantity || 1,
              price_per_unit: link.price_per_unit || undefined,
              comment: link.comment || undefined,
              total_uses: part.total_uses,
              avg_price: part.avg_price,
              last_used_at: part.last_used_at
            };
          });

          setSpareParts(parts);
          setInitialSpareParts(JSON.stringify(parts));
        } else {
          setInitialSpareParts('[]');
        }
        // Process reminders
        const { data: reminderResp } = await supabase.from('reminders').select('*').eq('note_id', noteId).maybeSingle();
        if (reminderResp) {
          const rData: ReminderFormData = {
            name: reminderResp.name || '',
            description: reminderResp.description || '',
            remind_date: reminderResp.remind_date,
            remind_time: reminderResp.remind_time,
            extra_reminders: reminderResp.extra_reminders,
            repeat_yearly: reminderResp.repeat_yearly,
            is_active: reminderResp.is_active,
            action_required: reminderResp.action_required
          };
          setReminderData(rData);
          setInitialReminderData(JSON.stringify(rData));
          setActualReminderId(reminderResp.id);
        } else {
          setInitialReminderData('null');
          setActualReminderId(null);
        }
      } else {
        // Reset for new note
        setContent('');
        setIsPublic(carData?.is_public || false);
        setIsPinned(false);
        setImages([]);
        setSpareParts([]);
        setReminderData(null);
        setActualReminderId(null);
        setInitialImages('[]');
        setInitialSpareParts('[]');
        setInitialReminderData('null');
        setInitialValues(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };


  // iOS Keyboard Fix: Use Visual Viewport API
  useEffect(() => {
    if (!window.visualViewport) return;

    const vvp = window.visualViewport;
    const handleViewportChange = () => {
      if (containerRef.current) {
        containerRef.current.style.height = `${vvp.height}px`;
        containerRef.current.style.top = `${vvp.offsetTop}px`;
        if (window.scrollY !== 0) {
          window.scrollTo(0, 0);
        }
      }
    };

    vvp.addEventListener('resize', handleViewportChange);
    vvp.addEventListener('scroll', handleViewportChange);
    handleViewportChange();

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100dvh';

    return () => {
      vvp.removeEventListener('resize', handleViewportChange);
      vvp.removeEventListener('scroll', handleViewportChange);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const handleSave = async () => {
    if (reminderData && reminderData.is_active) {
      const now = new Date();
      const remindDateTime = new Date(`${reminderData.remind_date}T${reminderData.remind_time || '00:00'}`);
      // Must be at least 1 minute in the future
      if (remindDateTime.getTime() <= now.getTime() + 60000) {
        setAlertConfig({ 
          isOpen: true, 
          title: t.common.error, 
          message: t.reminders.futureTimeError 
        });
        return;
      }
    }
    setSaving(true);
    try {
      const firstLine = (content || '').split('\n').find(line => line.trim()) || '';
      const derivedTitle = firstLine.replace(/[#*_`~]/g, '').trim().substring(0, 100) || 'Untitled Note';

      const noteData = {
        car_id: carId,
        user_id: effectiveUserId,
        title: derivedTitle,
        content: content.trim(),
        is_public: isPublic,
        is_pinned: isPinned,
        is_temporary: false,
        user_id: effectiveUserId
      };

      let currentNoteId = noteId;

      if (noteId) {
        const { error } = await supabase
          .from('notes')
          .update(noteData)
          .eq('id', noteId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('notes')
          .insert(noteData)
          .select()
          .single();
        if (error) throw error;
        currentNoteId = data.id;
      }

      if (currentNoteId) {
        const promises = [];

        // Handle Images (Parallel)
        promises.push((async () => {
          await supabase.from('note_images').delete().eq('note_id', currentNoteId);
          if (images.length > 0) {
            const imageRecords = images.map((img, index) => ({
              note_id: currentNoteId,
              image_url: img.url,
              image_url_thumb: img.thumbnail || img.url,
              comment: img.comment || '',
              display_order: index,
              user_id: effectiveUserId,
              file_size_bytes: img.sizeBytes || 0,
              is_receipt: img.is_receipt || false
            }));
            await supabase.from('note_images').insert(imageRecords);
          }
        })());

        // Handle Spare Parts (Parallel)
        promises.push((async () => {
          // Clear existing links first
          await supabase.from('note_spare_parts').delete().eq('note_id', currentNoteId);

          const validSpareParts = spareParts.filter(part => part.name.trim() !== '');

          // Process all parts in parallel
          await Promise.all(validSpareParts.map(async (part) => {
            // Upsert the part itself (Master Record)
            const { data: sparePartData, error: partError } = await supabase.from('spare_parts').upsert({
              id: part.id, // Use existing ID or undefined for new
              car_id: carId,
              name: part.name,
              manufacturer: part.manufacturer || null,
              part_code: part.part_code || null,
              link: part.link || null,
              comment: part.master_comment || null,
              user_id: effectiveUserId,
              is_temporary: false
            }).select().single();

            if (partError) throw partError;

            if (sparePartData) {
              const subPromises = [];

              // Create Link
              subPromises.push(supabase.from('note_spare_parts').insert({
                note_id: currentNoteId,
                spare_part_id: sparePartData.id,
                quantity: part.quantity || 1,
                price_per_unit: part.price_per_unit || null,
                comment: part.comment || null,
                added_at: new Date().toISOString()
              }));

              // Update images (Master Record images)
              if (part.images) {
                subPromises.push((async () => {
                  await supabase.from('spare_part_images').delete().eq('spare_part_id', sparePartData.id);
                  if (part.images.length > 0) {
                    const imageRecords = part.images.map((img: any, index: number) => ({
                      spare_part_id: sparePartData.id,
                      image_url: img.url,
                      image_url_thumb: img.thumbnail || img.url,
                      comment: img.comment || null,
                      display_order: index,
                      user_id: effectiveUserId,
                      file_size_bytes: img.sizeBytes || 0,
                      is_receipt: img.is_receipt || false,
                      log_entry_id: img.log_entry_id || null,
                      note_id: img.note_id || (img.log_entry_id ? null : currentNoteId)
                    }));
                    await supabase.from('spare_part_images').insert(imageRecords);
                  }
                })());
              }

              await Promise.all(subPromises);
            }
          }));
        })());

        // Handle Reminder (Parallel)
        promises.push((async () => {
          if (reminderData) {
            const rData: any = {
              car_id: carId,
              user_id: effectiveUserId,
              note_id: currentNoteId,
              name: reminderData.name || null,
              description: reminderData.description || null,
              remind_date: reminderData.remind_date,
              remind_time: reminderData.remind_time,
              extra_reminders: reminderData.extra_reminders,
              repeat_yearly: reminderData.repeat_yearly,
              is_active: reminderData.is_active
            };

            // Round to 5m on save
            if (rData.remind_time) {
              const [h, m] = rData.remind_time.split(':').map(Number);
              const roundedM = Math.round(m / 5) * 5;
              const finalM = roundedM === 60 ? 55 : roundedM;
              rData.remind_time = `${h.toString().padStart(2, '0')}:${finalM.toString().padStart(2, '0')}`;
            }
            
            // Try to find existing by note_id
            const { data: existing } = await supabase.from('reminders').select('id').eq('note_id', currentNoteId).maybeSingle();
            if (existing) {
              // Reset status if modified
              rData.is_seen = false;
              rData.action_required = false;
              rData.last_remind_sequence = 0;
              rData.updated_at = new Date().toISOString();
              await supabase.from('reminders').update(rData).eq('id', existing.id);
            } else {
              await supabase.from('reminders').insert(rData);
            }
          } else {
            // Delete if existed
            await supabase.from('reminders').delete().eq('note_id', currentNoteId);
          }
        })());

        await Promise.all(promises);
      }

      queryClient.invalidateQueries({ queryKey: NOTE_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: PART_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
      queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });

      onClose();
    } catch (error: any) {
      console.error('Error saving note:', error);
      let errorMessage = t.notes.saveFailed || 'Error saving note';
      if (error.code === '23514') {
        errorMessage = t.common.mandatoryFieldsError;
      }
      setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCall = async () => {
    if (!noteId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: NOTE_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: PART_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
      queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });

      onClose();
    } catch (error) {
      console.error('Error deleting note:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: t.notes.deleteFailed || 'Error deleting note' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const hasChanges = (() => {
    if (loading) return false;
    
    if (initialValues) {
      // Direct comparison for maximum reliability. Any char difference = change.
      const contentChanged = content !== (initialValues.content || '');
      const publicChanged = !!isPublic !== !!initialValues.isPublic;
      const pinnedChanged = !!isPinned !== !!initialValues.isPinned;
      
      const imagesChanged = JSON.stringify(images) !== initialImages;
      const partsChanged = JSON.stringify(spareParts) !== initialSpareParts;

      const normalizeReminder = (r: any) => {
        if (!r) return 'null';
        return `${r.name || ''}|${r.description || ''}|${r.remind_date || ''}|${(r.remind_time || '').substring(0, 5)}|${!!r.extra_reminders}|${!!r.repeat_yearly}|${!!r.is_active}`;
      };
      const reminderChanged = normalizeReminder(reminderData) !== normalizeReminder(initialReminderData !== 'null' ? JSON.parse(initialReminderData) : null);

      return contentChanged || publicChanged || pinnedChanged || imagesChanged || partsChanged || reminderChanged;
    } else {
      // For a new note, anything non-default is a change
      return (content || '').trim() !== '' || 
             isPinned || 
             images.length > 0 || 
             spareParts.length > 0 || 
             !!reminderData;
    }
  })();

  const handleSimulateReminder = async () => {
    if (!noteId || saving) return;
    setSaving(true);
    try {
      // Find the actual reminder ID for this note
      const { data: remData } = await supabase.from('reminders').select('id').eq('note_id', noteId).maybeSingle();
      if (!remData) {
        setAlertConfig({ isOpen: true, title: t.common.error, message: 'Nav iestatīts atgādinājums šai piezīmei' });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL || 'https://mycarcosts.gtreulands.workers.dev'}/reminders/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ reminder_id: remData.id })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText);
      }

      setAlertConfig({
        isOpen: true,
        title: t.common.ok,
        message: t.reminders.simulateSuccess
      });
    } catch (err: any) {
      console.error('Error simulating reminder:', err);
      setAlertConfig({
        isOpen: true,
        title: t.common.error,
        message: err.message || t.common.error
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    if (hasChanges && !isReadOnly && !saving) {
      setShowConfirmModal(true);
    } else {
      const unsavedUrls = images
        .map(img => img.url)
        .filter(url => url && !savedImageUrlsRef.current.has(url));
      if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center font-primary bg-gradient-theme">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 flex flex-col h-full overflow-hidden z-50 bg-gradient-theme"
      ref={containerRef}
    >
      <NavigationBar
        title={isReadOnly ? t.notes.viewNote : (noteId ? t.notes.editNote : t.notes.addNote)}
        onBack={handleBack}
        fixed={false}

        rightAction={(!isReadOnly && (!noteId || hasChanges)) ? {
          icon: saving ? Loader2 : Save,
          onClick: handleSave,
          disabled: saving,
          className: saving ? 'animate-spin' : ''
        } : undefined}
      />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
      >
        <div className="p-4 space-y-6 pb-20 max-w-2xl mx-auto">
          <RichTextEditor
            content={content}
            onChange={setContent}
            readOnly={isReadOnly}
            placeholder={t.notes.noteContentPlaceholder}
            className="flex-1"
          />



          {(images.length > 0 || !isReadOnly) && (
            <div>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.gallery.title}{images.length > 0 ? ` (${images.length})` : ''}
                </span>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => !isUploading && imageUploadRef.current?.openFileDialog()}
                    disabled={isUploading}
                    className={`rounded-full p-2 text-white transition-colors shadow-sm ${isUploading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-400"
                      }`}
                  >
                    {isUploading ? (
                      <Loader2 size={22} className="animate-spin" />
                    ) : (
                      <Plus size={22} strokeWidth={2.5} />
                    )}
                  </button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                {images.length === 0 && !isReadOnly && (
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">{t.dashboard.noFiles || 'Nav neviens fails pievienots'}</p>
                )}
                <ImageUpload
                  ref={imageUploadRef}
                  images={images}
                  onChange={setImages}
                  folder="notes"
                  maxImages={countLimit || 10}
                  showComments={true}
                  readOnly={isReadOnly}
                  type="note"
                  itemId={noteId}
                  hideUploadButton={true}
                  onUploadingChange={setIsUploading}
                  carId={carId}
                  showReceiptToggle={true}
                />
              </div>
            </div>
          )}

          {(spareParts.length > 0 || !isReadOnly) && (
            <div className="pt-4">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={theme.typography.sectionTitle}>
                  {t.spareParts.title}{spareParts.length > 0 ? ` (${spareParts.length})` : ''}
                </span>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => sparePartFormRef.current?.openSelector()}
                    className="bg-blue-600 dark:bg-blue-500 rounded-full p-2 text-white hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors shadow-sm"
                  >
                    <Plus size={22} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                {spareParts.length === 0 && !isReadOnly && (
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">
                    {t.spareParts.noPartsYet || 'Nav neviena rezerves detaļa pievienota'}
                  </p>
                )}
                <SparePartFormV2
                  ref={sparePartFormRef}
                  carId={carId}
                  spareParts={spareParts}
                  onChange={setSpareParts}
                  readOnly={isReadOnly}
                  preferences={car}
                  hideAddButton={true}
                  slug={slug || ''}
                  isAdminView={isAdminView}
                  currentNoteId={noteId}
                />
              </div>
            </div>
          )}

          <div className="pt-4">
            <ReminderFormSection
              data={reminderData}
              onChange={setReminderData}
              isReadOnly={isReadOnly}
              isAdminView={isAdminView}
              onSimulate={handleSimulateReminder}
              reminderId={actualReminderId || undefined}
              onMarkSeen={async () => {
                if (!actualReminderId || !reminderData) return;
                setSaving(true);
                try {
                  const updateData: any = { 
                    is_seen: true, 
                    action_required: false,
                    updated_at: new Date().toISOString()
                  };

                  if (reminderData.repeat_yearly && reminderData.remind_date) {
                    const currentDate = new Date(reminderData.remind_date);
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                    updateData.remind_date = currentDate.toISOString().split('T')[0];
                    updateData.is_seen = false;
                  }

                  await supabase.from('reminders').update(updateData).eq('id', actualReminderId);
                  await queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });
                  onClose();
                } catch (err) {
                  console.error('Error marking seen:', err);
                } finally {
                  setSaving(false);
                }
              }}
            />
          </div>

          {!isReadOnly && (
            <div className="pt-4">
              <div className="mb-2 px-1">
                <span className={theme.typography.sectionTitle}>{(t.common as any).additionalOptions || 'Papildu iespējas'}</span>
              </div>
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                {car?.is_public && (
                  <div className="flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {t.notes.publicNote}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isPublic}
                      onClick={() => !isReadOnly && setIsPublic(!isPublic)}
                      disabled={isReadOnly}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPublic ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'
                        } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPublic ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>
                )}


                <div className="flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {t.notes.pinToTop}
                    </span>
                    <Pin size={16} className="text-blue-600 dark:text-blue-400 fill-current" />
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPinned}
                    onClick={() => !isReadOnly && setIsPinned(!isPinned)}
                    disabled={isReadOnly}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPinned ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'
                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPinned ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {noteId && !isReadOnly && (
            <div className="pt-4">
              <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full text-left p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center justify-between"
                >
                  {t.notes.deleteNote}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Extra space for keyboard */}
        <div className="h-[40vh]" />
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title={t.notes.deleteNote}
        message={t.notes.deleteConfirm}
        confirmLabel={t.common.delete}
        cancelLabel={t.common.cancel}
        onConfirm={handleDeleteCall}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        isDestructive={true}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        title={t.common.unsavedChangesTitle}
        message={t.common.unsavedChangesMessage}
        confirmLabel={t.common.save}
        cancelLabel={t.common.dontSave}
        onConfirm={() => {
          handleSave();
          setShowConfirmModal(false);
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          const unsavedUrls = images.map(img => img.url).filter(url => url && !savedImageUrlsRef.current.has(url));
          if (unsavedUrls.length > 0) deleteTempFiles(unsavedUrls);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        isDestructive={false}
      />
      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.ok}
        onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
      />
    </div>
  );
}
